import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import PageWrapper from '../components/PageWrapper';
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  deleteDoc
} from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { useActiveSession } from '../ActiveSessionContext';
import { normalizeKey, normalizeType, normalizeGenre } from '../utils/normalize';
import BorderedBox from '../components/BorderedBox';
import TuneCardBox from '../components/TuneCardBox';
import StyledCheckbox from '../components/StyledCheckbox';
import myIcon from '../assets/logo512.png'; // adjust path
import dexie from '../offlineDB';
import { flushPendingWrites } from '../utils/flushPendingWrites'; // ✅ Make sure this is imported
import { useUserPrefs } from '../UserPrefsProvider';
import TagFilterControl from '../components/TagFilterControl';
import CustomTagInput from '../components/CustomTagInput';

const SessionViewer = () => {
  const [tunes, setTunes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSessionKeys, setSelectedSessionKeys] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const { user } = useAuth();
  const { inputStyle, textStyle } = useUI();
  const { activeSession, setActiveSession } = useActiveSession();
  const navigate = useNavigate();
const [collapsed, setCollapsed] = useState(false);
const [filterQuery, setFilterQuery] = useState('');
const [filterGenre, setFilterGenre] = useState('');
const [selectedTags, setSelectedTags] = useState([]);
const [tagLogic, setTagLogic] = useState('AND');
const [sortOrder, setSortOrder] = useState('newest');
const [showTagFilterUI, setShowTagFilterUI] = useState(false);
const [bulkTagInputVisible, setBulkTagInputVisible] = useState(false);
const [bulkTags, setBulkTags] = useState([]);

const { prefs } = useUserPrefs();

 useEffect(() => {
  if (!user) return;

  const preload = async () => {
    try {
      if (navigator.onLine) {
        await flushPendingWrites(user); // ✅ flush any pending writes first
      }
    } catch (err) {
      console.warn("Pending write flush failed:", err);
    }

    const sessionsRef = collection(db, 'users', user.uid, 'sessions');
    const tunesRef = collection(db, 'users', user.uid, 'tunes');

    if (navigator.onLine) {
      const unsubscribeSessions = onSnapshot(sessionsRef, async (sessionSnap) => {
        const sessionDocs = sessionSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const tuneUnsub = onSnapshot(tunesRef, (tuneSnapshot) => {
          const allTunes = tuneSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          // 🧠 Save both to local Dexie
          dexie.tunes.bulkPut(allTunes).catch(() => {});
          dexie.sessions.bulkPut(sessionDocs).catch(() => {});

          const sessionsWithTunes = sessionDocs.map(session => {
            const tunesInSession = allTunes.filter(tune =>
              Array.isArray(tune.sessions) &&
              tune.sessions.some(s => s.sessionId === session.id)
            );

            return {
              id: session.id,
              location: session.location,
startedAt: session.startedAt instanceof Date
  ? session.startedAt
  : typeof session.startedAt?.toDate === 'function'
    ? session.startedAt.toDate()
    : typeof session.startedAt === 'string'
      ? new Date(session.startedAt)
      : null,
              endedAt: session.endedAt instanceof Date
                ? session.endedAt
                : session.endedAt?.toDate?.() || (session.endedAt ? new Date(session.endedAt) : null),
              tunes: tunesInSession
            };
          });

 setTunes(allTunes);
setSessions(sessionsWithTunes);

        });

        return () => tuneUnsub();
      });

      return () => unsubscribeSessions();
    } else {
      // Offline fallback
      Promise.all([
        dexie.tunes.toArray(),
        dexie.sessions.toArray()
      ]).then(([allTunes, sessionDocs]) => {
        const sessionsWithTunes = sessionDocs.map(session => {
          const tunesInSession = allTunes.filter(tune =>
            Array.isArray(tune.sessions) &&
            tune.sessions.some(s => s.sessionId === session.id)
          );

          return {
            id: session.id,
            location: session.location,
            startedAt: session.startedAt || new Date(),
            endedAt: session.endedAt || null,
            tunes: tunesInSession
          };
        });
setTunes(allTunes);
setSessions(sessionsWithTunes);

      });
    }
  };

  preload();
}, [user]);



  const toggleSessionSelection = (key) => {
    setSelectedSessionKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSessionKeys.length === sessions.length) {
      setSelectedSessionKeys([]);
    } else {
      setSelectedSessionKeys(sessions.map(s => s.id))
    }
  };

const deleteSelectedSessions = async () => {
  if (!user || selectedSessionKeys.length === 0) return;

  const containsActive = selectedSessionKeys.includes(activeSession?.id);
  if (containsActive) {
    window.alert('Cannot delete the currently active session. Please end the session first.');
    return;
  }

  const confirmDelete = window.confirm(`Delete ${selectedSessionKeys.length} session(s)?`);
  if (!confirmDelete) return;

  // Show undo banner immediately
 setPendingDelete({
  tuneBackups: [],
  sessionKeys: selectedSessionKeys,
  sessionBackups: sessions.filter(s => selectedSessionKeys.includes(s.id))
});

  setSelectedSessionKeys([]);
  setSessions(s => [...s]);

  const timer = setTimeout(() => {
    setPendingDelete(null);
    setUndoTimer(null);
  }, 5000);
  setUndoTimer(timer);

  const affectedTuneSnapshots = [];

  // 🔥 Delete each session
  for (const sessionId of selectedSessionKeys) {
    if (navigator.onLine) {
      await deleteDoc(doc(db, 'users', user.uid, 'sessions', sessionId));
    } else {
      await dexie.sessions.delete(sessionId);
      await dexie.pendingWrites.put({
        id: `${sessionId}__delete`,
        type: 'delete',
        collection: 'sessions',
        docId: sessionId,
        data: {}
      });
    }
  }

  // 🎯 Clean up all affected tunes
  for (const tune of tunes) {
    const ref = doc(db, 'users', user.uid, 'tunes', tune.id);

    let original;
    if (navigator.onLine) {
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      original = snap.data();
    } else {
      original = await dexie.tunes.get(tune.id);
      if (!original) continue;
    }

    const updatedSessions = (original.sessions || []).filter(s => {
      const startedAtMillis = s.startedAt && typeof s.startedAt.toMillis === 'function'
        ? s.startedAt.toMillis()
        : s.timestamp && typeof s.timestamp.toMillis === 'function'
        ? s.timestamp.toMillis()
        : s.startedAt || s.timestamp || 0;

      return !selectedSessionKeys.includes(s.sessionId);
    });

    if (updatedSessions.length !== (original.sessions || []).length) {
      affectedTuneSnapshots.push({ id: tune.id, data: original });

      await dexie.tunes.update(tune.id, { sessions: updatedSessions });

      await dexie.pendingWrites.put({
        id: `${tune.id}__sessionRemove`,
        type: 'update',
        collection: 'tunes',
        docId: tune.id,
        data: { sessions: updatedSessions }
      });

      if (navigator.onLine) {
        await updateDoc(ref, { sessions: updatedSessions });
      }
    }
  }

  // Fill in tune backups after processing
setPendingDelete(prev => {
  if (!prev) return null;
  return {
    ...prev,
    tuneBackups: affectedTuneSnapshots
  };
});

};


const deleteSession = async () => {
  if (!selectedSession || !user) return;

  if (selectedSession.id === activeSession?.id) {
    window.alert('Cannot delete the currently active session. Please end the session first.');
    return;
  }

  const confirmDelete = window.confirm(`Delete session at ${selectedSession.location}?`);
  if (!confirmDelete) return;

  // Show undo banner immediately
setPendingDelete({
  tuneBackups: [],
  sessionKeys: [selectedSession.id],
  sessionBackups: [selectedSession]
});

  setSelectedSession(null);
  setActiveSession(null);

  const timer = setTimeout(() => {
    setPendingDelete(null);
    setUndoTimer(null);
  }, 5000);
  setUndoTimer(timer);

  const affectedTuneSnapshots = [];

  // Step 1: Delete the session itself
  if (navigator.onLine) {
    await deleteDoc(doc(db, 'users', user.uid, 'sessions', selectedSession.id));
  } else {
    await dexie.sessions.delete(selectedSession.id);
    await dexie.pendingWrites.put({
      id: `${selectedSession.id}__delete`,
      type: 'delete',
      collection: 'sessions',
      docId: selectedSession.id,
      data: {}
    });
  }

  // Step 2: Remove this session from all relevant tunes
  for (const tune of selectedSession.tunes) {
    const ref = doc(db, 'users', user.uid, 'tunes', tune.id);

    let original;
    if (navigator.onLine) {
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      original = snap.data();
    } else {
      original = await dexie.tunes.get(tune.id);
      if (!original) continue;
    }

    const updatedSessions = (original.sessions || []).filter(s => {
      const sStartedAtMillis = s.startedAt && typeof s.startedAt.toMillis === 'function'
        ? s.startedAt.toMillis()
        : s.timestamp && typeof s.timestamp.toMillis === 'function'
        ? s.timestamp.toMillis()
        : s.startedAt || s.timestamp || 0;

      const selStartedAtMillis = selectedSession.startedAt && typeof selectedSession.startedAt.getTime === 'function'
        ? selectedSession.startedAt.getTime()
        : selectedSession.timestamp && typeof selectedSession.timestamp.toMillis === 'function'
        ? selectedSession.timestamp.toMillis()
        : selectedSession.startedAt || selectedSession.timestamp || 0;

      return sStartedAtMillis !== selStartedAtMillis;
    });

    if (updatedSessions.length !== (original.sessions || []).length) {
      affectedTuneSnapshots.push({ id: tune.id, data: original });

      const cleanedData = updatedSessions.length > 0
        ? { sessions: updatedSessions }
        : { sessions: [] };

      await dexie.tunes.update(tune.id, cleanedData);

      await dexie.pendingWrites.put({
        id: `${tune.id}__sessionRemove`,
        type: 'update',
        collection: 'tunes',
        docId: tune.id,
        data: cleanedData
      });

      if (navigator.onLine) {
        await updateDoc(ref, cleanedData);
      }
    }
  }

  // Fill in tune backups after processing
setPendingDelete(prev => {
  if (!prev) return null;
  return {
    ...prev,
    tuneBackups: affectedTuneSnapshots
  };
});

};

const undoDelete = async () => {
  if (!user || !pendingDelete) return;

  if (undoTimer) {
    clearTimeout(undoTimer);
    setUndoTimer(null);
  }

  // Restore deleted sessions
  for (const backup of pendingDelete.sessionBackups || []) {
    const id = backup.id;
    const sessionRef = doc(db, 'users', user.uid, 'sessions', id);

    if (navigator.onLine) {
      await setDoc(sessionRef, backup);
    }

    await dexie.sessions.put({ ...backup, id });

    await dexie.pendingWrites.put({
      id: `${id}__undo`,
      type: 'update',
      collection: 'sessions',
      docId: id,
      data: backup
    });
  }

  // Restore affected tunes
  for (const { id, data } of pendingDelete.tuneBackups) {
    const ref = doc(db, 'users', user.uid, 'tunes', id);

    if (navigator.onLine) {
      await setDoc(ref, data);
    }

    await dexie.tunes.put({ ...data, id });

    await dexie.pendingWrites.put({
      id: `${id}__undo`,
      type: 'update',
      collection: 'tunes',
      docId: id,
      data
    });
  }

  // ✅ Force visual refresh
setSessions(prev => {
  const restored = pendingDelete.sessionBackups.filter(
    restored => !prev.some(s => s.id === restored.id)
  );
  return [...prev, ...restored];
});

  setPendingDelete(null);
  setUndoTimer(null);
};

const getFilteredSessions = () => {
  const matches = [];

  for (const session of sessions) {
    const query = filterQuery.toLowerCase();

    let matchingTuneTitle = null;

    const matchesSearch = !query || (
      (session.location?.toLowerCase().includes(query)) ||
      (session.notes?.toLowerCase().includes(query)) ||
      session.tunes?.some(t => {
        if (t.title?.toLowerCase().includes(query)) {
          matchingTuneTitle = t.title;
          return true;
        }
        return false;
      })
    );

const matchesGenre = !filterGenre || session.tunes?.some(t => normalizeGenre(t.genre) === filterGenre);

    const matchesTags = selectedTags.length === 0 || (
      tagLogic === 'AND'
        ? selectedTags.every(tag => (session.customTags || []).includes(tag))
        : selectedTags.some(tag => (session.customTags || []).includes(tag))
    );

    if (matchesSearch && matchesGenre && matchesTags) {
      matches.push({ ...session, _matchedTuneTitle: matchingTuneTitle });
    }
  }

  return matches.sort((a, b) => {
    if (sortOrder === 'alpha') return (a.location || '').localeCompare(b.location || '');
    const aTime = a.startedAt?.getTime?.() || 0;
    const bTime = b.startedAt?.getTime?.() || 0;
    return sortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
  });
};


const filteredSessions = getFilteredSessions();



  if (selectedSession) {
    return (
      <PageWrapper>
<button className="big-button" onClick={() => {
  setSelectedSession(null); // just leave activeSession alone
}}>
  ← Sessions
</button>

<h2>
  {selectedSession.location}
  {activeSession?.id === selectedSession.id && (
    <span style={{ fontStyle: 'italic', color: 'green', marginLeft: '0.5ch' }}>
      (currently active)
    </span>
  )}
</h2>
        <p style={textStyle}>{selectedSession.startedAt instanceof Date ? selectedSession.startedAt.toLocaleString() : new Date(selectedSession.startedAt || selectedSession.timestamp || 0).toLocaleString()}</p>
<div style={{
  marginBottom: '20px',
  display: 'flex',
  justifyContent: 'center',
  gap: '1rem'
}}>
  <button className="big-button" onClick={() => navigate(`/editsession/${selectedSession.id}`)}>
    Edit Session
  </button>
  <button className="big-button" onClick={deleteSession}>
    Delete Session
  </button>
</div>



        <div style={{ maxHeight: "500px", overflowY: "auto", paddingRight: "10px", marginBottom: "2rem" }}>
          <ul className="card-list">
            {selectedSession.tunes.map(tune => (
              <li
                key={tune.id}
                className="card"
                style={{
                  cursor: 'pointer',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--card-border)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '10px'
                }}
                onClick={() => {
                  const sessionTunes = selectedSession.tunes.map(t => ({ ...t, id: String(t.id) }));
                  const currentIndex = sessionTunes.findIndex(t => String(t.id) === String(tune.id));

                  navigate(`/tune/${tune.id}`, {
                    state: {
                      tune,
                      sessionTunes,
                      sessionTitle: selectedSession.location,
                      currentIndex
                    }
                  });
                }}


              >
<strong>{tune.title}</strong> — {tune.genre} / {tune.type} / {normalizeKey(tune.key)}{tune.status ? ` (${tune.status})` : ''}
              </li>
            ))}
          </ul>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>




<BorderedBox
  style={{ marginBottom: '0.7rem' }}
  collapsible
  defaultVisible
  exposeCollapsed={setCollapsed}
>

  <div className="app-title" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
    <img src={myIcon} alt="Logo" className="responsive-icon" />
    <span>AllMySessions</span>
  </div>

<div style={{ maxWidth: '600px', margin: '0 auto' }}>
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: '0.7rem',
      maxWidth: '750px',
      margin: '0 auto 1rem auto',
      alignItems: 'center',
    }}
  >
    <input
      className="search-input"
      placeholder="Search title or any metadata..."
      value={filterQuery}
      onChange={(e) => setFilterQuery(e.target.value)}
      style={{ ...inputStyle, gridColumn: '1 / -1' }}
    />

    <select
      style={{ ...inputStyle, minWidth: 0 }}
      className={filterGenre ? "filter-active" : ""}
      value={filterGenre}
      onChange={(e) => setFilterGenre(e.target.value)}
    >
      <option value="">All Genres</option>
      {[...new Set(tunes.map(t => normalizeGenre(t.genre)))].filter(Boolean).sort()
.map(g => (
        <option key={g} value={g}>
          {g}
        </option>
      ))}
    </select>

    <button
      onClick={() => setShowTagFilterUI(v => !v)}
      style={{
        ...inputStyle,
        backgroundColor: selectedTags.length > 0 ? '#228be6' : '#444',
        color: 'white',
      }}
    >
      {showTagFilterUI
        ? `✖ Hide Tags (${selectedTags.length})`
        : `🏷️ Filter by Tags`}
    </button>

    <button
      onClick={() => {
        setFilterQuery('');
        setFilterGenre('');
        setSelectedTags([]);
        setTagLogic('AND');
        setShowTagFilterUI(false);
      }}
      style={{
        ...inputStyle,
        backgroundColor: '#444',
        color: 'white',
      }}
    >
      🚫 Clear Filters
    </button>

{showTagFilterUI && prefs?.customTags?.length > 0 && (
  <div
    style={{
      gridColumn: '1 / -1',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '0.75rem',
      maxWidth: '1000px',
      margin: '0 auto 1rem auto',
    }}
  >
    <TagFilterControl
      availableTags={prefs.customTags}
      selectedTags={selectedTags}
      setSelectedTags={setSelectedTags}
      tagLogic={tagLogic}
      setTagLogic={setTagLogic}
    />
  </div>
)}


    <select
      style={inputStyle}
      value={sortOrder}
      onChange={(e) => setSortOrder(e.target.value)}
    >
      <option value="newest">Newest first</option>
      <option value="oldest">Oldest first</option>
      <option value="alpha">A–Z</option>
    </select>

    <button
      onClick={toggleSelectAll}
      style={{ ...inputStyle, backgroundColor: '#444', color: 'white' }}
    >
      {selectedSessionKeys.length === filteredSessions.length
        ? 'Deselect All'
        : 'Select All'}
    </button>

    <select
      style={inputStyle}
      value=""
      onChange={(e) => {
        const value = e.target.value;
        if (!value || selectedSessionKeys.length === 0) return;
        if (value === 'delete') {
          deleteSelectedSessions();
        } else if (value === 'addtags') {
          setBulkTagInputVisible(true);
        }
      }}
    >
      <option value="">
        {selectedSessionKeys.length > 0
          ? `Bulk Actions on ${selectedSessionKeys.length}`
          : 'Bulk Actions…'}
      </option>
      <option value="delete">🗑️ Delete Selected</option>
      <option value="addtags">🏷️ Add Tags</option>
    </select>
  </div>
</div>




{bulkTagInputVisible && (
  <div
    style={{
      marginTop: '0.5rem',
      padding: '0.5rem',
      borderTop: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
      Add tags to selected sessions:
    </p>

    <div
      style={{
        width: '100%',
        maxWidth: '400px',
        boxSizing: 'border-box',
        padding: '0 1rem',
      }}
    >
      <CustomTagInput
        tags={bulkTags}
        setTags={setBulkTags}
        suggestions={prefs?.customTags || []}
        inputStyle={inputStyle}
      />
    </div>

    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem' }}>
      <button
        onClick={async () => {
          for (const id of selectedSessionKeys) {
            const session = sessions.find(s => s.id === id);
            const existing = session?.customTags || [];
            const final = Array.from(new Set([...existing, ...bulkTags]));
            if (navigator.onLine) {
              await updateDoc(doc(db, 'users', user.uid, 'sessions', id), { customTags: final });
            } else {
              await dexie.sessions.update(id, { customTags: final });
              await dexie.pendingWrites.put({
                id: `${id}__tagUpdate`,
                type: 'update',
                collection: 'sessions',
                docId: id,
                data: { customTags: final }
              });
            }
          }
          setBulkTags([]);
          setBulkTagInputVisible(false);
          setSelectedSessionKeys([]);
        }}
        style={{
          padding: '6px 12px',
          fontSize: '0.8rem',
          borderRadius: '6px',
          backgroundColor: '#228be6',
          color: 'white'
        }}
      >
        ✅ Confirm Tags
      </button>

      <button
        onClick={() => {
          setBulkTags([]);
          setBulkTagInputVisible(false);
        }}
        style={{ fontSize: '0.8rem' }}
      >
        Cancel
      </button>
    </div>
  </div>
)}



</BorderedBox>

{pendingDelete && (
  <div style={{
    position: 'fixed',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#ffdddd',
    padding: '12px 20px',
    border: '1px solid #cc0000',
    borderRadius: '8px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    zIndex: 1000,
    color: '#000'
  }}>
    <strong>Sessions deleted.</strong> 
    <button
      onClick={undoDelete}
      style={{
        marginLeft: '1rem',
        padding: '6px 12px',
        backgroundColor: '#fff',
        border: '1px solid #cc0000',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      Undo
    </button>
  </div>
)}


<div
  style={{
    overflowY: 'auto',
    boxSizing: 'border-box',
    height: `calc(100vh - ${collapsed ? 180 : 250}px)`,
    paddingRight: '0px',
    paddingLeft: '0px',
    marginBottom: '2rem',
  }}
>

  {sessions.length === 0 ? (
    <p>No sessions recorded yet.</p>
  ) : (
    <ul className="card-list">
  {filteredSessions.map((s) => (
        <TuneCardBox
          key={s.id}
          onClick={() => {
            setSelectedSession(s);
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flex: '1 1 200px',
                minWidth: '160px'
              }}
            >
              <StyledCheckbox
                checked={selectedSessionKeys.includes(s.id)}
                onChange={() => toggleSessionSelection(s.id)}
                onClick={(e) => e.stopPropagation()}
              />

              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: '0.5ch'
                  }}
                >
                  <strong style={{ fontSize: '1rem' }}>
                    {s.location || s.startedAt.toLocaleString()}
                    {activeSession?.id === s.id && (
                      <span style={{ fontStyle: 'italic', color: 'green', marginLeft: '0.5ch' }}>
                        (currently active)
                      </span>
                    )}
                  </strong>

                </div>
                <div
                  style={{
                    fontSize: '0.9rem',
                    color: 'var(--text-muted)',
                    marginTop: '2px'
                  }}
                >
         {s._matchedTuneTitle && (
<div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' }}>
  🎯 Includes tune: <strong>{s._matchedTuneTitle}</strong>
</div>

)}
        
        {(() => {
  const start = s.startedAt instanceof Date
    ? s.startedAt
    : new Date(s.startedAt || s.timestamp || 0);

  const formatted = start.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });

if (!s.endedAt) return `${formatted} — (ongoing)`;

  const end = s.endedAt instanceof Date
    ? s.endedAt
    : new Date(s.endedAt);

  const durationMs = end - start;
  const totalMinutes = Math.max(1, Math.floor(durationMs / 60000)); // ensure ≥ 1 min
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const durationLabel = [
    hours > 0 ? `${hours} hr${hours > 1 ? 's' : ''}` : null,
    minutes > 0 ? `${minutes} min` : null
  ].filter(Boolean).join(', ');

  return `${formatted} — ${durationLabel}`;
})()}



                </div>
              </div>
            </div>
          </div>
        </TuneCardBox>
      ))}
    </ul>
  )}
</div>


    </PageWrapper>
  );
};

export default SessionViewer;
